import { OrgOption } from '@weenat/client/dist/hooks/useAllUsersOrgs'
import { useIntl } from '@weenat/wintl'
import useDisclosure from 'app/src/hooks/useDisclosure'
import Collapsible from 'app/src/kit/Collapsible'
import Icons from 'app/src/kit/Icons'
import Link from 'app/src/kit/LinkComponent'
import LoadingCircle from 'app/src/kit/loaders/LoadingCircle'
import MenuItem from 'app/src/kit/MenuItem'
import Text from 'app/src/kit/Text'
import { isEmpty, isNil } from 'lodash-es'
import { useMemo } from 'react'
import { styled } from 'styled-components'

const DisplayText = styled(Text)`
  overflow: hidden;
  text-align: center;
`

const CollapsibleGroupHeader = styled(Flex)`
  padding: ${(p) => p.theme.spacings.md}px;
  position: sticky;
  top: 0;
  left: 0;
  background: ${(p) => p.theme.colors.grayscale['100']};
  border-bottom: ${(p) => p.theme.borderWidths.sm}px solid ${(p) => p.theme.colors.grayscale['300']};
  & > * {
    color: ${(p) => p.theme.colors.grayscale['700']};
    font-size: ${(p) => p.theme.typography.sizes.sm}px;
    font-weight: ${(p) => p.theme.typography.weights.medium};
  }
  &:hover {
    cursor: pointer;
    background: ${(p) => p.theme.colors.grayscale['300']};
    & > * {
      color: ${(p) => p.theme.colors.grayscale['900']};
    }
  }
`

const StyledFlex = styled(Flex)<{ $isOpen: boolean }>`
  flex-direction: column;
  &:not(:last-child) {
    border-bottom: ${(p) =>
      p.$isOpen ? p.theme.borderWidths.sm + 'px solid ' + p.theme.colors.grayscale['300'] : 'none'};
  }
`

const OrgMenuItem = styled(MenuItem)<{ $isSelected: boolean }>`
  &::after {
    background-color: ${(p) => (p.$isSelected ? p.theme.colors.primary['200'] : '')};
  }
`

interface CollapsibleFarmSection {
  label: string
  farms: OrgOption[]
  selectedFarm: OrgOption | undefined
  /** Make sure this is memoized since its linked to the renderItem */
  onFarmSelection: (farmId: number) => Promise<void>
}

/**
 * Component that render organizations in a collapsible list of Links
 */
const CollapsibleFarmSection = ({
  label,
  farms,
  onFarmSelection,
  selectedFarm
}: CollapsibleFarmSection) => {
  const { isOpen, toggle } = useDisclosure(true)

  return (
    <>
      <CollapsibleGroupHeader onClick={toggle}>
        <Text>{label}</Text>
        <Icons.ArrowDropDown $rotate={isOpen ? 0 : -90} />
      </CollapsibleGroupHeader>
      <StyledFlex $isOpen={isOpen}>
        <Collapsible isOpen={isOpen} direction='vertical' $py='sm'>
          <Flex $flexDirection='column' $gap='sm'>
            {farms.map((option) => {
              const onItemPress = () => onFarmSelection(option.value)

              return (
                <Link key={option.value}>
                  <OrgMenuItem
                    onPress={onItemPress}
                    $isSelected={!isNil(selectedFarm) && selectedFarm.value === option.value}
                  >
                    <Flex $alignItems='center' $px='lg' $py='md' $gap='sm'>
                      <Text>{option.label}</Text>
                      {option.isMainNetworkFarm ? <Icons.NewReleases /> : null}
                    </Flex>
                  </OrgMenuItem>
                </Link>
              )
            })}
          </Flex>
        </Collapsible>
      </StyledFlex>
    </>
  )
}

interface FarmsListProps {
  farms: OrgOption[]
  selectedFarm: OrgOption | undefined
  /** Make sure this is memoized since its linked to the renderItem */
  onFarmSelection: (farmId: number) => Promise<void>
  isLoading?: boolean
  searchQuery?: string
}

const FarmsList = ({
  farms,
  isLoading,
  onFarmSelection,
  selectedFarm,
  searchQuery
}: FarmsListProps) => {
  const { t } = useIntl()

  const sections = useMemo(() => {
    const farmsByNetwork = farms.reduce<Record<string, OrgOption[]>>((acc, option) => {
      const groupLabel = option.networkName
      if (isNil(acc[groupLabel])) {
        acc[groupLabel] = []
      }
      acc[groupLabel].push(option)
      return acc
    }, {})
    return Object.entries(farmsByNetwork).map(([title, data]) => ({ title, data }))
  }, [farms])

  return (
    <Flex $flexDirection='column' $position='relative'>
      {isLoading ? (
        <Flex $flex={1} $flexDirection='column' $py='lg' $px='lg' $gap='xl'>
          <LoadingCircle size='xl' />
          <Text $textAlign='center'>{t('status.loading')}</Text>
        </Flex>
      ) : !isEmpty(farms) ? (
        <>
          {sections.map(({ title, data }) => (
            <CollapsibleFarmSection
              key={title}
              label={title}
              farms={data}
              onFarmSelection={onFarmSelection}
              selectedFarm={selectedFarm}
            />
          ))}
        </>
      ) : (
        <Box $py='lg' $px='lg'>
          <DisplayText>
            {t('map.no_summaries_matching_search_term', {
              searchTerm: !isNil(searchQuery) ? searchQuery : ''
            })}
          </DisplayText>
        </Box>
      )}
    </Flex>
  )
}

export default FarmsList
